import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AvatarState } from '../types';

const initialState: AvatarState = {
  provider: "anam",
  isConnected: false,
  isConnecting: false,
  isSpeaking: false,
  error: null,
  isEnabled: false,
  currentAvatarId: null,
  streamActive: false,
};

const avatarSlice = createSlice({
  name: 'avatar',
  initialState,
  reducers: {
    setProvider: (state, action: PayloadAction<"anam" | "tavus">) => {
      if (state.isConnected || state.isConnecting) return;
      state.provider = action.payload;
      state.error = null;
    },
    // Connection management
    startConnecting: (state, action: PayloadAction<{ avatarId: string }>) => {
      state.isConnecting = true;
      state.error = null;
      state.currentAvatarId = action.payload.avatarId;
    },
    connectSuccess: (state) => {
      state.isConnected = true;
      state.isConnecting = false;
      state.error = null;
    },
    connectFailure: (state, action: PayloadAction<string>) => {
      state.isConnected = false;
      state.isConnecting = false;
      state.error = action.payload;
      state.currentAvatarId = null;
      state.streamActive = false;
      state.isSpeaking = false;
    },
    disconnect: (state) => {
      console.log('Avatar slice: disconnect action - cleaning up state');
      state.isConnected = false;
      state.isConnecting = false;
      state.isSpeaking = false;
      state.currentAvatarId = null;
      state.streamActive = false;
      // Keep error for debugging, but don't clear it automatically
      // state.error = null;
    },
    
    // Enable/disable
    setEnabled: (state, action: PayloadAction<boolean>) => {
      state.isEnabled = action.payload;
      if (!action.payload) {
        // If disabled, clear all connection state
        state.isConnected = false;
        state.isConnecting = false;
        state.isSpeaking = false;
        state.currentAvatarId = null;
        state.streamActive = false;
      }
    },
    
    // Stream management
    streamReady: (state) => {
      state.streamActive = true;
    },
    streamEnded: (state) => {
      state.streamActive = false;
    },
    
    // Speaking state
    startSpeaking: (state) => {
      if (state.isConnected) {
        state.isSpeaking = true;
      }
    },
    stopSpeaking: (state) => {
      state.isSpeaking = false;
    },

    // Avatar interruption
    interrupt: (state, action: PayloadAction<{ reason?: string }>) => {
      const { reason } = action.payload || {};
      console.log('Avatar slice: interrupt action:', { reason, wasSpeaking: state.isSpeaking });
      
      // The actual interruption is handled by middleware
      // This just logs the action for debugging
      if (state.isSpeaking) {
        console.log('🛑 Avatar interrupt requested via slice action');
        // The middleware will handle calling the avatar provider interrupt.
        // and then call stopSpeaking() to update the state
      } else {
        console.log('🛑 Avatar interrupt requested but avatar not speaking');
      }
    },
    
    // Error handling
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      // If there's an error, we might not be connected anymore
      if (state.error.includes('disconnect') || state.error.includes('timeout')) {
        state.isConnected = false;
        state.isConnecting = false;
        state.isSpeaking = false;
        state.streamActive = false;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    
    // Force reset state (for when things go really wrong)
    forceReset: (state) => {
      console.log('Avatar slice: force reset - returning to initial state');
      return { ...initialState, provider: state.provider, isEnabled: state.isEnabled };
    },
    
    // Regular reset (maintains enabled state)
    reset: (state) => {
      console.log('Avatar slice: reset - cleaning up connection state');
      state.isConnected = false;
      state.isConnecting = false;
      state.isSpeaking = false;
      state.error = null;
      state.currentAvatarId = null;
      state.streamActive = false;
    },
  },
});

export const {
  setProvider,
  startConnecting,
  connectSuccess,
  connectFailure,
  disconnect,
  setEnabled,
  streamReady,
  streamEnded,
  startSpeaking,
  stopSpeaking,
  interrupt,
  setError,
  clearError,
  forceReset,
  reset,
} = avatarSlice.actions;

export default avatarSlice.reducer;
